/**
 * Accounting domain types.
 *
 * Proper double-entry bookkeeping:
 *
 *   Chart of Accounts (COA) — numbered, typed ledger accounts
 *   Journal Entry          — dated header
 *   Journal Line           — debit OR credit against an account
 *
 * Balanced-entry invariant: ∑ debits = ∑ credits per entry.
 * Enforced in both the form (save button disabled until balanced)
 * and the backend (rejects unbalanced entries).
 *
 * Normal balances follow accounting convention:
 *   assets, expenses        → debit normal
 *   liabilities, equity, revenue → credit normal
 *
 * Period state:
 *   open   → can post, edit, delete entries
 *   closed → read-only; requires owner to reopen
 */

// ─────────── Account types ───────────

export type AccountType =
  | 'asset'
  | 'liability'
  | 'equity'
  | 'revenue'
  | 'expense';

export const ACCOUNT_TYPES: AccountType[] = ['asset', 'liability', 'equity', 'revenue', 'expense'];

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  asset:     'Asset',
  liability: 'Liability',
  equity:    'Equity',
  revenue:   'Revenue',
  expense:   'Expense',
};

/** Whether a type's natural balance is on the debit or credit side */
export const NORMAL_BALANCE: Record<AccountType, 'debit' | 'credit'> = {
  asset:     'debit',
  liability: 'credit',
  equity:    'credit',
  revenue:   'credit',
  expense:   'debit',
};

/** Standard numbering convention for the chart */
export const ACCOUNT_RANGES: Record<AccountType, [number, number]> = {
  asset:     [1000, 1999],
  liability: [2000, 2999],
  equity:    [3000, 3999],
  revenue:   [4000, 4999],
  expense:   [5000, 9999],
};

// ─────────── Account ───────────

export type Account = {
  id: string;
  number: string;             // '1000', '4100', etc. — strings to preserve leading zeros
  name: string;
  type: AccountType;
  subtype?: string;            // 'Current Asset', 'Long-term Liability', 'COGS', etc.
  description?: string;
  parentId?: string;           // for sub-accounts (e.g. 1200.1 under 1200)
  active: boolean;
  /** Balance derived from journal entries — not stored on the account doc
   *  but filled in by the backend for list views. */
  balance?: number;
  createdAt?: string;
  updatedAt?: string;
};

// ─────────── Journal ───────────

export type JournalLine = {
  id: string;                  // client-generated uuid for stable keys
  accountId: string;
  accountNumber?: string;      // denormalized for display
  accountName?: string;
  debit?: number;              // exactly one of debit|credit should be > 0
  credit?: number;
  description?: string;
};

export type JournalEntryStatus = 'draft' | 'posted' | 'reversed';

export const JOURNAL_STATUS_LABELS: Record<JournalEntryStatus, string> = {
  draft:    'Draft',
  posted:   'Posted',
  reversed: 'Reversed',
};

export type JournalEntrySource =
  | 'manual'         // a human entered it in the Accounting UI
  | 'invoice'        // auto-generated from invoice.create
  | 'payment'        // auto-generated from payment.record
  | 'inventory'      // auto from inventory movement
  | 'payroll'        // reserved for future
  | 'adjusting';     // year-end / month-end adjustments

export type JournalEntry = {
  id: string;
  number: string;              // 'JE-0001'
  status: JournalEntryStatus;
  source: JournalEntrySource;

  date: string;                // ISO date (YYYY-MM-DD) — the effective period date
  memo: string;
  reference?: string;          // external document reference

  lines: JournalLine[];

  totalDebit: number;
  totalCredit: number;

  // Source linkage (when source !== 'manual')
  sourceDocType?: 'invoice' | 'payment' | 'order' | 'movement';
  sourceDocId?: string;
  sourceDocNumber?: string;

  // Reversal tracking
  reversedBy?: string;         // id of the reversing entry
  reverses?: string;            // id of the entry this one reverses

  createdAt?: string;
  createdBy?: string;
  createdByName?: string;
  postedAt?: string;
  postedBy?: string;
  updatedAt?: string;
};

// ─────────── Fiscal period ───────────

export type FiscalPeriod = {
  id: string;                  // '2026-Q1', '2026-03', '2026'
  label: string;
  startDate: string;
  endDate: string;
  status: 'open' | 'closed';
  closedAt?: string;
  closedBy?: string;
  closedByName?: string;
};

// ─────────── Reports ───────────

export type TrialBalanceRow = {
  accountId: string;
  accountNumber: string;
  accountName: string;
  accountType: AccountType;
  totalDebit: number;
  totalCredit: number;
  balance: number;             // positive for normal-side balance
};

export type TrialBalance = {
  asOf: string;
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
};

// ─────────── Helpers ───────────

export function sumLines(lines: JournalLine[]) {
  const totalDebit = lines.reduce((s, l) => s + (l.debit ?? 0), 0);
  const totalCredit = lines.reduce((s, l) => s + (l.credit ?? 0), 0);
  return {
    totalDebit,
    totalCredit,
    balanced: Math.abs(totalDebit - totalCredit) < 0.005 && totalDebit > 0,
    difference: totalDebit - totalCredit,
  };
}

export function normalSideOf(type: AccountType) {
  return NORMAL_BALANCE[type];
}

/** Compute an account's net balance given its type and the sum of debits/credits */
export function netBalance(type: AccountType, totalDebit: number, totalCredit: number) {
  return NORMAL_BALANCE[type] === 'debit'
    ? totalDebit - totalCredit
    : totalCredit - totalDebit;
}
